import { Container, Download, ExternalLink, MonitorPlay, ScrollText, Square, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { create } from "zustand";
import type { LocalRun } from "@ide/shared/desktop";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Dialog, DialogContent, Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlay";
import { Spinner, StatusDot } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { cn } from "@/lib/utils";
import { isActive, refreshEngine, runsAvailable, stopRun, useLocalRuns, type RunProject } from "./local-runs";

/** Diálogos das execuções (qualquer página). */
export const useRunsUi = create<{
  /** "Rodar na sua máquina" do projeto. */
  dialog: RunProject | null;
  /** Saída da execução. */
  logs: string | null;
  menu: boolean;
}>(() => ({ dialog: null, logs: null, menu: false }));

export const openRunDialog = (project: RunProject) => useRunsUi.setState({ dialog: project, menu: false });
export const openRunLogs = (runId: string) => useRunsUi.setState({ logs: runId, menu: false });

const STATUS: Record<LocalRun["status"], string> = {
  starting: "preparando…",
  running: "subindo…",
  stopping: "parando…",
  exited: "parado",
  failed: "falhou",
};

function statusText(run: LocalRun) {
  if (run.status === "running" && run.reachable) return "no ar";
  if (run.status === "running" && run.kind === "live") return "no ar";
  return STATUS[run.status];
}

function dotState(run: LocalRun): "on" | "off" | "busy" {
  if (isActive(run)) return run.reachable ? "on" : "busy";
  return run.status === "stopping" ? "busy" : "off";
}

/** Uma execução: projeto, tipo, endereço e ações. */
export function RunRow({ run, showProject = true }: { run: LocalRun; showProject?: boolean }) {
  const Icon = run.kind === "live" ? MonitorPlay : Container;
  return (
    <li className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-bg-muted">
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-bg text-fg-muted">
        <Icon className="h-3.5 w-3.5" />
        <StatusDot state={dotState(run)} className="absolute -bottom-0.5 -right-0.5 ring-2 ring-surface" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] text-fg">
          {showProject ? <span className="font-medium">{run.projectName}</span> : null}
          {showProject ? <span className="text-fg-subtle"> · </span> : null}
          <span className={showProject ? "text-fg-muted" : "font-medium"}>{run.label}</span>
        </p>
        <p className="truncate text-[11.5px] text-fg-subtle">
          {run.status === "failed" && run.detail ? run.detail : statusText(run)}
          {run.url && isActive(run) ? (
            <>
              {" · "}
              <button
                onClick={() => desktop?.runs.open(run.id)}
                className="font-mono text-fg-muted underline-offset-2 hover:text-fg hover:underline"
              >
                {run.url.replace(/^http:\/\//, "").replace(/\/$/, "")}
              </button>
            </>
          ) : null}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {run.kind === "container" ? (
          <Tip label="Saída">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => openRunLogs(run.id)}
              aria-label="Ver a saída"
            >
              <ScrollText className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        ) : null}
        {run.url && isActive(run) ? (
          <Tip label="Abrir no navegador">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => desktop?.runs.open(run.id)}
              aria-label="Abrir"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        ) : null}
        {isActive(run) ? (
          <Tip label="Parar">
            <Button variant="ghost" size="icon-sm" onClick={() => void stopRun(run.id)} aria-label="Parar">
              <Square className="h-3 w-3" />
            </Button>
          </Tip>
        ) : run.status !== "stopping" ? (
          <Tip label="Tirar da lista">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => desktop?.runs.dismiss(run.id)}
              aria-label="Tirar da lista"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        ) : null}
      </div>
    </li>
  );
}

/** Estado do Kompil Engine, com instalar, ativar o WSL e remover. */
export function EngineCard({ compact }: { compact?: boolean }) {
  const engine = useLocalRuns((s) => s.engine);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    void refreshEngine();
  }, []);
  if (!desktop?.engine) return null;
  if (!engine || engine.state === "checking") {
    return (
      <div className="flex items-center gap-2 text-[12px] text-fg-muted">
        <Spinner className="h-3.5 w-3.5" /> Verificando os contêineres…
      </div>
    );
  }
  if (engine.state === "ready") {
    return (
      <div className="flex items-center gap-2 text-[12px] text-fg-muted">
        <StatusDot state="on" />
        <span className="min-w-0 flex-1 truncate">Contêineres prontos</span>
        {engine.backend === "wsl" && !compact ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
              <Trash2 className="h-3.5 w-3.5" /> Remover
            </Button>
            <ConfirmDialog
              open={confirm}
              onOpenChange={setConfirm}
              title="Desativar os contêineres?"
              description="Apaga as imagens e os contêineres baixados. Os projetos não são afetados; dá para ativar de novo quando quiser."
              actionLabel="Desativar"
              danger
              onConfirm={() => void desktop?.engine.remove()}
            />
          </>
        ) : null}
      </div>
    );
  }
  if (engine.state === "installing") {
    const p = engine.progress;
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-[12px] text-fg">
          <Spinner className="h-3.5 w-3.5" />
          <span className="flex-1 truncate">{p?.step ?? "Preparando…"}</span>
          {p?.percent != null ? <span className="font-mono text-fg-muted">{p.percent}%</span> : null}
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-bg-emphasis">
          <div
            className={cn(
              "h-full rounded-full bg-fg transition-[width]",
              p?.percent == null && "w-1/3 animate-pulse",
            )}
            style={p?.percent != null ? { width: `${p.percent}%` } : undefined}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border border-border bg-bg p-3">
      <p className="flex items-center gap-2 text-[12.5px] font-medium text-fg">
        <Container className="h-3.5 w-3.5" /> Contêineres sem Docker Desktop
      </p>
      <p className="text-[12px] leading-relaxed text-fg-muted">{engine.detail}</p>
      <div className="flex flex-wrap gap-1.5">
        {engine.state === "unsupported" && engine.canEnableWsl ? (
          <Button size="sm" variant="primary" onClick={() => void desktop?.engine.enableWsl()}>
            Ativar no Windows
          </Button>
        ) : null}
        {(engine.state === "missing" || engine.state === "error") && engine.backend === "wsl" ? (
          <Button size="sm" variant="primary" onClick={() => void desktop?.engine.install()}>
            <Download className="h-3.5 w-3.5" />{" "}
            {engine.state === "error" ? "Tentar de novo" : "Ativar contêineres"}
          </Button>
        ) : null}
        {engine.state === "error" && engine.backend !== "wsl" && desktop.platform === "win32" ? (
          <Button size="sm" variant="primary" onClick={() => void desktop?.engine.install()}>
            Tentar de novo
          </Button>
        ) : null}
        {desktop.platform !== "win32" && engine.state === "missing" ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => desktop?.openExternal("https://podman.io/docs/installation")}
          >
            Como instalar <ExternalLink className="h-3 w-3 opacity-60" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const ANSI = /\x1b\[[0-9;?]*[ -/]*[@-~]/g; // eslint-disable-line no-control-regex
const clean = (s: string) => s.replace(ANSI, "").replace(/\r\n/g, "\n");

/** Saída de uma execução, ao vivo. */
export function RunLogDialog() {
  const runId = useRunsUi((s) => s.logs);
  const run = useLocalRuns((s) => s.runs.find((r) => r.id === runId) ?? null);
  const [text, setText] = useState("");
  const box = useRef<HTMLPreElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    if (!runId || !desktop?.runs) return;
    let alive = true;
    // Posição já mostrada: eventos e histórico se sobrepõem (os eventos saem em lotes).
    let shown = -1;
    const early: Array<{ text: string; end: number }> = [];
    const add = (text: string, end: number) => {
      if (end <= shown) return;
      const fresh = text.slice(Math.max(0, text.length - (end - shown)));
      shown = end;
      setText((t) => t + clean(fresh));
    };
    const off = desktop.runs.onLog((e) => {
      if (e.runId !== runId) return;
      if (shown < 0) early.push(e);
      else add(e.text, e.end);
    });
    void desktop.runs.logs(runId).then((all) => {
      if (!alive) return;
      shown = all.end;
      setText(clean(all.text));
      for (const e of early) add(e.text, e.end);
    });
    return () => {
      alive = false;
      off();
      setText("");
    };
  }, [runId]);

  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [text]);

  return (
    <Dialog open={Boolean(runId)} onOpenChange={(v) => !v && useRunsUi.setState({ logs: null })}>
      <DialogContent
        wide
        title={run ? `Saída — ${run.projectName}` : "Saída"}
        description={run ? `${run.label} · ${statusText(run)}${run.url ? ` · ${run.url}` : ""}` : undefined}
      >
        <pre
          ref={box}
          onScroll={(e) => {
            const el = e.currentTarget;
            stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          }}
          className="h-[52vh] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-canvas p-3 font-mono text-[12px] leading-relaxed text-fg"
        >
          {text || "Aguardando a saída…"}
        </pre>
        <div className="mt-3 flex items-center justify-end gap-2">
          {run?.url && isActive(run) ? (
            <Button size="sm" variant="secondary" onClick={() => desktop?.runs.open(run.id)}>
              <ExternalLink className="h-3.5 w-3.5" /> Abrir{" "}
              {run.url.replace(/^http:\/\//, "").replace(/\/$/, "")}
            </Button>
          ) : null}
          {run && isActive(run) ? (
            <Button size="sm" variant="danger" onClick={() => void stopRun(run.id)}>
              <Square className="h-3 w-3" /> Parar
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Barra de título: as execuções desta máquina (de todos os projetos), em qualquer página. */
export function RunsButton() {
  const runs = useLocalRuns((s) => s.runs);
  const open = useRunsUi((s) => s.menu);
  if (!runsAvailable) return null;
  const live = runs.filter(isActive);
  return (
    <Popover open={open} onOpenChange={(menu) => useRunsUi.setState({ menu })}>
      <Tip label={live.length ? `${live.length} rodando na sua máquina` : "Execuções na sua máquina"}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Execuções locais"
            className={cn(
              "relative flex h-8 w-8 items-center justify-center rounded-md text-fg-muted outline-none transition-colors hover:bg-bg-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-ring/60 data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg",
              // Nada rodando: o botão some (a lista abre pelo menu Layout, ancorada aqui).
              !live.length && "pointer-events-none -ml-1.5 w-0 overflow-hidden opacity-0",
            )}
            tabIndex={live.length ? undefined : -1}
          >
            <Container className="h-4 w-4" />
            {live.length ? (
              <span className="absolute right-0.5 top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-fg px-0.5 text-[9.5px] font-semibold text-fg-inverse">
                {live.length}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent
        align="end"
        className="w-[380px] p-0"
        // Aberto por outro menu (Executar ▸ Execuções desta máquina): o foco que volta para aquele
        // menu não pode fechar esta lista na hora. Clicar fora continua fechando.
        onFocusOutside={(e) => e.preventDefault()}
      >
        <div className="border-b border-border px-3.5 py-2.5">
          <p className="text-[13px] font-semibold text-fg">Rodando na sua máquina</p>
          <p className="text-[11.5px] text-fg-subtle">
            Só você vê e controla. O time continua editando o mesmo código.
          </p>
        </div>
        {runs.length ? (
          <ul className="max-h-[320px] overflow-y-auto p-1.5">
            {runs.map((r) => (
              <RunRow key={r.id} run={r} />
            ))}
          </ul>
        ) : (
          <p className="px-3.5 py-5 text-center text-[12.5px] leading-relaxed text-fg-muted">
            Nada rodando. Dentro de um projeto, use{" "}
            <span className="text-fg">Executar ▸ Rodar na sua máquina</span> para pré-visualizar o site ou
            subir um contêiner — vários projetos ao mesmo tempo.
          </p>
        )}
        <div className="border-t border-border px-3.5 py-2.5">
          <EngineCard compact />
        </div>
      </PopoverContent>
    </Popover>
  );
}
